import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TopBar from './layout/TopBar';
import BottomNav from './BottomNav';
import Phone from './Phone';
import { C } from './theme';
import { fmtCurrency, useDrillAuth } from './drillHelpers';
import { TipsShimmer, Bone } from './Shimmer';
import { useDesktopTopBar } from '../context/DesktopLayoutContext';
import { useIsDesktop } from './reports/reportTokens';
import { todayStr, yesterdayStr, getStartEndFromFilter } from '../services/reportsApi';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { fetchLiveByChannelRequest, fetchLiveStaffPerformanceRequest } from '../redux/reports/clickHouse/clickHouseActions';
import type { LiveByChannelItem, LiveStaffPerformanceItem } from '../interface/clickHouseInterface';
import SectionError from './reports/SectionError';
import SectionEmpty from './reports/SectionEmpty';

const getCanonicalChannel = (channel: string | null): string => {
    const key = (channel ?? '').toLowerCase().replace(/[\s\-_]+/g, '');
    if (/dinein/.test(key)) return 'Dine-In';
    if (/pickup/.test(key)) return 'Pickup';
    if (/delivery/.test(key)) return 'Delivery';
    if (/instore|inhouse/.test(key)) return 'Instore';
    return '3rd Party';
};

const DrillTips = ({ onBack, initialDateFilter, initialDate, snapshotValues: snapshotValuesProp }: { onBack?: () => void; initialDateFilter?: string; initialDate?: string; snapshotValues?: any } = {} as any) => {
    const isEmbedded = !!onBack;
    const navigate = useNavigate();
    const isDesktop = useIsDesktop();

    useEffect(() => {
        if (!isEmbedded && isDesktop) navigate('/reports/snapshot?drill=tips', { replace: true });
    }, [isDesktop, navigate, isEmbedded]);
    const setTopBarProps = useDesktopTopBar();
    const { locationId, selectedDate: drillDate, dateFilter: drillDateFilter, snapshotValues: snapshotValuesAuth } = useDrillAuth();
    // Embedded (mobile SnapshotScreen) passes snapshotValues via prop; standalone reads it from navigation state.
    const snapshotValues = snapshotValuesProp ?? snapshotValuesAuth;
    const today = useMemo(() => todayStr(), []);
    const yesterday = useMemo(() => yesterdayStr(), []);
    const [selectedDate, setSelectedDate] = useState(initialDate ?? drillDate ?? today);
    const [activeDateFilter, setActiveDateFilter] = useState(initialDateFilter ?? drillDateFilter ?? 'Today');
    const dateRange = useMemo(() => {
        const range = getStartEndFromFilter(activeDateFilter, today, yesterday);
        return range || { startDate: selectedDate, endDate: selectedDate };
    }, [activeDateFilter, selectedDate, today, yesterday]);

    const isToday = activeDateFilter === 'Today';
    // ClickHouse date params — omitted for Today (live path), passed only for historical ranges
    const chDates: { startDate?: string; endDate?: string } = isToday ? {} : { startDate: dateRange.startDate, endDate: dateRange.endDate };

    const dispatch = useAppDispatch();
    const liveStaffPerformance: LiveStaffPerformanceItem[] = useAppSelector((s) => s.clickHouse.liveStaffPerformanceSuccess);
    const liveStaffPerformanceLoading: boolean = useAppSelector((s) => s.clickHouse.liveStaffPerformanceLoading);
    const liveStaffPerformanceFailure: boolean = useAppSelector((s) => s.clickHouse.liveStaffPerformanceFailure);
    const liveByChannel: LiveByChannelItem[] = useAppSelector((s) => s.clickHouse.liveByChannelSuccess);
    const liveByChannelLoading: boolean = useAppSelector((s) => s.clickHouse.liveByChannelLoading);
    const liveByChannelFailure: boolean = useAppSelector((s) => s.clickHouse.liveByChannelFailure);

    const [loading, setLoading] = useState(!isToday);

    const handleDateFilterChange = useCallback((opt: string) => {
        setActiveDateFilter(opt);
        if (opt === 'Today') setSelectedDate(today);
        else if (opt === 'Yesterday') setSelectedDate(yesterday);
    }, [today, yesterday]);

    useEffect(() => {
        if (isEmbedded) return;
        setTopBarProps({
            title: 'Tips',
            onBack: () => navigate('/reports/snapshot'),
            dateFilters: { value: activeDateFilter, onChange: handleDateFilterChange },
        });
    }, [setTopBarProps, activeDateFilter, handleDateFilterChange, navigate, isEmbedded]);

    // Standalone dispatch (embedded: parent SnapshotScreen dispatches). Always ClickHouse
    // staff-performance, passing startDate/endDate so historical date filters work too.
    useEffect(() => {
        if (!locationId) {
            setLoading(false);
            return;
        }
        if (!isEmbedded) {
            dispatch(fetchLiveStaffPerformanceRequest({ locationId, ...chDates }));
        }
        setLoading(false);
    }, [locationId, isEmbedded, dateRange.startDate, dateRange.endDate, dispatch]);

    // Derive totals from liveStaffPerformance Redux data (Today + historical via startDate/endDate)
    const staffPerfTotal = liveStaffPerformance.reduce((s, item) => s + (item.tip ?? 0), 0);
    const staffPerfOrderCount = liveStaffPerformance.reduce((s, item) => s + (item.orderCount ?? 0), 0);
    const staffPerfNetSales = liveStaffPerformance.reduce((s, item) => s + (item.netSales ?? 0), 0);
    // Avg Tip per Order = (Σ Tip) / (Σ Order Count)
    const avgPerOrderTip = staffPerfOrderCount > 0 ? staffPerfTotal / staffPerfOrderCount : 0;
    // Tip Rate = (Σ Tip / Σ Net Sales) × 100
    const tipRatePct = staffPerfNetSales > 0 ? (staffPerfTotal / staffPerfNetSales) * 100 : 0;

    // Tips by Channel — use liveByChannel Redux data (/sales/live/v2/by-channel)
    // Unknown channels are grouped under "3rd Party"
    const channelTipRows = useMemo(() => {
        const map = new Map<string, number>();
        liveByChannel.forEach((item) => {
            if ((item.tip ?? 0) <= 0) return;
            const canonical = getCanonicalChannel(item.channel);
            map.set(canonical, (map.get(canonical) ?? 0) + (item.tip ?? 0));
        });
        const total = Array.from(map.values()).reduce((s, v) => s + v, 0);
        return Array.from(map.entries())
            .sort(([, a], [, b]) => b - a)
            .map(([channel, tip]) => ({
                channel,
                tip,
                pct: total > 0 ? Math.round((tip / total) * 100) : 0,
            }));
    }, [liveByChannel]);

    // Tips by Staff — group by staff_name, sum tip
    const staffTipRows = useMemo(() => {
        const map = new Map<string, number>();
        liveStaffPerformance.forEach((item) => {
            const name = item.staff_name || 'Unknown';
            map.set(name, (map.get(name) ?? 0) + (item.tip ?? 0));
        });
        return Array.from(map.entries())
            .filter(([, tipVal]) => tipVal > 0)
            .sort(([, a], [, b]) => b - a)
            .slice(0, 6)
            .map(([name, tip]) => ({ name, tip }));
    }, [liveStaffPerformance]);

    // Highest tip = full bar (matches desktop). No artificial floor of 1, which would
    // shrink the bar to ~0% when the single/top tip is a tiny value (e.g. $0.02).
    const maxStaffTip = staffTipRows[0]?.tip || 1;

    const isDataLoading = liveStaffPerformanceLoading || loading;
    const subTitle = isDataLoading ? null : `${fmtCurrency(staffPerfTotal)} total`;

    const inner = isDataLoading ? <TipsShimmer /> : (<>
        {subTitle && <div style={{ fontSize: 10, color: C.t3, padding: '0 16px 4px' }}>{subTitle}</div>}
        <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px 16px' }}>
            {/* Tips Stamp KPIs: Total Tips, Avg/Order, Tip Rate */}
            {liveStaffPerformanceFailure ? (
                <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 12, marginBottom: 12, boxShadow: C.shadow }}>
                    <SectionError
                        kpiName="tips summary"
                        onRetry={() => dispatch(fetchLiveStaffPerformanceRequest({ locationId, ...chDates }))}
                        size="small"
                    />
                </div>
            ) : liveStaffPerformance.length === 0 ? (
                <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 12, marginBottom: 12, boxShadow: C.shadow }}>
                    <SectionEmpty kpiName="tips summary" size="small" />
                </div>
            ) : (
                <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                    <div style={{ flex: 1, background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 10, padding: '8px 6px', textAlign: 'center', boxShadow: C.shadow }}>
                        <div style={{ fontSize: 7, color: C.t3, textTransform: 'uppercase' }}>Total Tips</div>
                        <div style={{ fontSize: 16, fontWeight: 700, color: C.green, marginTop: 2 }}>
                            {fmtCurrency(staffPerfTotal)}
                        </div>
                    </div>
                    <div style={{ flex: 1, background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 10, padding: '8px 6px', textAlign: 'center', boxShadow: C.shadow }}>
                        <div style={{ fontSize: 7, color: C.t3, textTransform: 'uppercase' }}>Avg/Order</div>
                        <div style={{ fontSize: 16, fontWeight: 700, color: C.t1, marginTop: 2 }}>
                            {fmtCurrency(avgPerOrderTip)}
                        </div>
                    </div>
                    <div style={{ flex: 1, background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 10, padding: '8px 6px', textAlign: 'center', boxShadow: C.shadow }}>
                        <div style={{ fontSize: 7, color: C.t3, textTransform: 'uppercase' }}>Tip Rate</div>
                        <div style={{ fontSize: 16, fontWeight: 700, color: C.t1, marginTop: 2 }}>
                            {`${tipRatePct.toFixed(1)}%`}
                        </div>
                        <div style={{ fontSize: 7, color: C.t3, marginTop: 1 }}>of Net Sales</div>
                    </div>
                </div>
            )}

            {(<>
                {/* Tips by Staff */}
                <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 14, padding: 14, marginBottom: 12, boxShadow: C.shadow }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: C.t1, marginBottom: 2 }}>Tips by Staff</div>
                    <div style={{ fontSize: 10, color: C.t3, marginBottom: 10 }}>Performance ranking</div>
                    {liveStaffPerformanceFailure ? (
                        <SectionError
                            kpiName="tips by staff"
                            onRetry={() => dispatch(fetchLiveStaffPerformanceRequest({ locationId, ...chDates }))}
                            size="small"
                        />
                    ) : staffTipRows.length ? staffTipRows.map((s, i) => (
                        <div key={i} style={{ marginBottom: i < staffTipRows.length - 1 ? 8 : 0 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                                <span style={{ fontSize: 11, color: C.t1, fontWeight: 500 }}>{s.name}</span>
                                <div style={{ display: 'flex', gap: 8 }}>
                                    <span style={{ fontSize: 10, color: C.t3 }}>
                                        {staffPerfTotal > 0 ? `${((s.tip / staffPerfTotal) * 100).toFixed(1)}%` : '—'}
                                    </span>
                                    <span style={{ fontSize: 11, fontWeight: 600, color: C.t1, width: 38, textAlign: 'right' }}>{fmtCurrency(s.tip)}</span>
                                </div>
                            </div>
                            <div style={{ height: 5, background: C.hover, borderRadius: 3, overflow: 'hidden' }}>
                                <div style={{ height: '100%', width: `${maxStaffTip > 0 ? (s.tip / maxStaffTip) * 100 : 0}%`, background: C.green, borderRadius: 3 }} />
                            </div>
                        </div>
                    )) : (
                        <SectionEmpty kpiName="tips by staff" size="small" />
                    )}
                </div>

                {/* Tips by Channel — data from /sales/live/v2/by-channel */}
                <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 14, padding: 14, boxShadow: C.shadow }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: C.t1, marginBottom: 2 }}>Tips by Channel</div>
                    <div style={{ fontSize: 10, color: C.t3, marginBottom: 10 }}>Revenue source distribution</div>
                    {liveByChannelLoading ? (
                        <div>
                            {[1, 2, 3].map(i => (
                                <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 0', borderBottom: i < 3 ? `1px solid ${C.divider}` : 'none' }}>
                                    <Bone w={70} h={12} r={4} />
                                    <Bone w={40} h={12} r={4} />
                                </div>
                            ))}
                        </div>
                    ) : liveByChannelFailure ? (
                        <SectionError
                            kpiName="tips by channel"
                            onRetry={() => dispatch(fetchLiveByChannelRequest({ locationId, ...chDates }))}
                            size="small"
                        />
                    ) : channelTipRows.length ? channelTipRows.map((ch, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', padding: '6px 0', gap: 8, borderBottom: i < channelTipRows.length - 1 ? `1px solid ${C.divider}` : 'none' }}>
                            <span style={{ flex: 1, fontSize: 12, color: C.t1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ch.channel}</span>
                            <span style={{ width: 36, fontSize: 10, color: C.t3, flexShrink: 0, textAlign: 'right' }}>{ch.pct}%</span>
                            <span style={{ width: 72, fontSize: 12, fontWeight: 600, color: C.t1, flexShrink: 0, textAlign: 'right' }}>{fmtCurrency(ch.tip)}</span>
                        </div>
                    )) : (
                        <SectionEmpty kpiName="tips by channel" size="small" />
                    )}
                </div>
            </>)}
        </div>
    </>);

    if (isEmbedded) return <>{inner}</>;
    return (
        <Phone>
            <TopBar variant="mobile" />
            {inner}
            <BottomNav />
        </Phone>
    );
};

export default DrillTips;
